import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from '@/components/ui/badge';
import { BookOpen, Loader2 } from "lucide-react";
import { useTranslation } from 'react-i18next';
import '../features/admin/components/ManualContentStyles.css';

interface ManualDialogProps {
  children: React.ReactNode;
  manualLanguage?: string;
}

interface ManualContent {
  id: number;
  title: string;
  content: string;
  version: string;
  language: string;
  updated_at: string;
}

export function ManualDialog({ children, manualLanguage }: ManualDialogProps) {
  const { t, i18n } = useTranslation('common');
  const [manual, setManual] = useState<ManualContent | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadManual = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Použijeme předaný jazyk manuálu, nebo aktuální jazyk aplikace
      const language = manualLanguage || (i18n.language || 'cs').split('-')[0];
      const response = await fetch(`/api/manual?language=${language}`);
      if (!response.ok) {
        throw new Error(t('errors.loading_manual'));
      }
      
      const data = await response.json();
      if (data.success && data.data) {
        setManual(data.data);
      } else {
        throw new Error(t('errors.loading_manual'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.unexpected'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild onClick={loadManual}>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            {manual?.title || t('dashboard.internal_manual')}
          </DialogTitle>
          <DialogDescription>
            {manual?.version ? `${t('manual_editor.version')} ${manual.version}` : t('app.loading')}
            {manual?.updated_at && (
              <span className="ml-2 text-xs">
                {t('manual_editor.last_updated')}: {new Date(manual.updated_at).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'cs-CZ')}
              </span>
            )}
            {manual?.language && (
              <span className="ml-2">
                <Badge variant="outline" className="text-xs">
                  {manual.language === 'cs' ? '🇨🇿 Čeština' : '🇺🇸 English'}
                </Badge>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="h-full max-h-[60vh] overflow-y-auto pr-4">
          {isLoading && (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="ml-2">{t('app.loading')}...</span>
            </div>
          )}
          
          {error && (
            <div className="text-center text-red-600 p-4">
              <p>{error}</p>
            </div>
          )}
          
          {manual?.content && (
            <div className="space-y-6 text-sm">
              <div 
                className="prose prose-sm max-w-none manual-content"
                dangerouslySetInnerHTML={{ __html: manual.content }}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
} 